import { gsap } from "gsap"
import { ScrollToPlugin } from "gsap/ScrollToPlugin"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import Flickity from "flickity"
import utilities from '@/scripts/common/utilities'
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)
import { refreshAllControllers, addScrollController } from "@/scripts/common/api/scrolltrigger.services"

export const Carousels = (() => {

    class FlickityCarousel {
        constructor(container, options) {
            this.$slider = container
            this.$slides = this.$slider.querySelectorAll('[class*="__item"]')
            this.$pagination = null
            this.arrowModifiers = null
            this.asNavFor = container.dataset.asNav || null
            this.goToPanel = !!container.dataset.gotoPanel || false
            this.isMobile = window.innerWidth < site.settings.breakpoints.mobile
            this.syncLocation = !!container.dataset.syncLocation || false
            this.carousel = null
            this.scrollController = null
            this.scrollerWatch = window
            this.initialized = false
            this.parallaxBg = this.$slider.dataset.parallaxBg || false
            this.parallaxContent = this.$slider.dataset.parallaxContent || false
            this.carouselForMobileOnly = this.$slider.hasAttribute("data-carousel-for-mobile") || false

            this.toggleClasses = {
                singleSlide: 'carousel--one-slide'
            }

            this.options = Object.assign({
                freeScroll: false,
                contain: true,
                asNavFor: this.asNavFor,
                lazyLoad: 2,
                bgLazyLoad: 2
            }, {
                ...options,
                ...this.$slider.dataset.carouselOptions && JSON.parse(this.$slider.dataset.carouselOptions)
            })

            this.scrollOptions = Object.assign({
                scroller: this.scrollerWatch,
                trigger: this.$slider,
                start: () => 'top bottom',
            })

            this.parallaxLayerOptions = {
                cellRatio: 0.6,
                bgRatio: 0.8,
                fgRatio: 1.25
            }


        }

        // Init function
        init() {
            this.initCallback()
            this._addlisteners()
            this.$slider.getCarousel = () => this
        }

        // Init function
        initCallback() {
            if (this.options.lazyLoad) {
                this.$slides.forEach($s => {
                    const $imgWrapper = $s.querySelector('.bg-img')
                    $imgWrapper?.classList.add('bg-img--preload')
                })
            }
        }

        // List Class Adders
        listClassTogglers() {
            (this.$slides.length === 1) ? this.$slider.classList.add(this.toggleClasses['singleSlide']) : this.$slider.classList.remove(this.toggleClasses['singleSlide'])
        }

        // Options extending
        updateOptions(options) {
            Object.assign(this.options, {
                ...options
            })
        }



        // Utilities
        scrollToPanel() {
            if (this.goToPanel) {
                const $items = gsap.utils.toArray(this.$slides)
                $items.forEach($item => {
                    $item.addEventListener('click', () => {
                        gsap.to(window, {
                            duration: 1,
                            scrollTo: {
                                y: this.asNavFor,
                                offsetY: 100
                            },
                            ease: 'power2.inOut'
                        })
                    })
                })
            }
        }

        pauseAllInactiveVideos() {
            const selectedIndex = this.carousel.selectedIndex
            this.$slides.forEach(($slide, i) => {
                const videoBannerVideo = $slide.querySelector('.banner-video') ? $slide.querySelector('.banner-video').getVideo() : null
                if( videoBannerVideo && videoBannerVideo.videoPlaying) videoBannerVideo._pauseVideo()
            })
        }

        moveParallaxLayer(layer, layerRatio, progress) {
            const ratio = this.parallaxLayerOptions.cellRatio * layerRatio
            layer.style['left'] = ( 0.5 - ( 0.5 + progress * count ) * ratio ) * 100 + '%'
        }

        // Destroy carousel
        _destroy() {
            this.carousel.destroy()
        }

        // Recalculate and refresh carousel
        _refresh() {
            this.carousel?.resize()
            refreshAllControllers()
        }

        // Append
        _append(elements /* elements array */) {
            this.carousel.append(elements)
        }
        // prepend
        _prepend(elements /* elements array */) {
            this.carousel.prepend(elements)
        }
        // Remove
        _remove(elements /* elements array */) {
            this.carousel.remove(elements)
        }

        // Building carousel
        _makeCarousel() {
            if (this.carousel) this._destroy()

            if(this.carouselForMobileOnly) {
                if(this.isMobile) {
                    this.carousel = new Flickity(this.$slider, {
                        ...this.options,
                        on: {
                            ready: () => this._onSliderReady()
                        }
                    })
                    return
                }
                return
            }

            this.carousel = new Flickity(this.$slider, {
                ...this.options,
                on: {
                    ready: () => this._onSliderReady()
                }
            })

        }

        // ResizeFunc
        _resizeCallback() {
            return utilities.debounce(() => {
                this.isMobile = window.innerWidth < site.settings.breakpoints.mobile
                this._onResize()
            }, 250)
        }

        // Parallaxes
        _bgParallax(progress) {
            this.carousel.slides.forEach((slide, i) => {
                
                var background = this.$slides[i].querySelector('.background')
                if(!background) return
                var x = (slide.target + this.carousel.x) * -1 / 3
                background.style['transform'] = 'translateX(' + x + 'px)'
            })
        }

        _contentParallax(progress) {
            this.carousel.slides.forEach((slide, i) => {
                
                var content = this.$slides[i].querySelector('.parallax-content')
                if(!content) return
                var x = (slide.target + this.carousel.x) * 1 / 2
                content.style['transform'] = 'translateX(' + x + 'px)'
                // content.style['opacity'] = 1 + (x/1000) 
            })
        }

        _layerParallax(progress) {
            if(this.parallaxLayers.length === 0) return
            this.parallaxLayers.forEach((layer, i) => moveParallaxLayer(layer, this.parallaxLayerOptions.bgRatio, progress))
        }

        // callbacks
        _onResize() {
            return false
        }
        _onSliderReady() {
            this.listClassTogglers()
        }

        _onLazyload($cell) {
            const $imgWrapper = $cell.querySelector('.bg-img')
            $imgWrapper.classList.remove('bg-img--preload')
            $imgWrapper.classList.add('bg-img--loaded')
            refreshAllControllers()
        }

        _onCarouselScroll(evt, progress) {
            if (this.parallaxBg) this._bgParallax(progress)
            if (this.parallaxContent && !this.isMobile) this._contentParallax(progress)
            if (this.parallaxLayer) this._layerParallax(progress)
        }

        _onSlideChange(index) {
            this.pauseAllInactiveVideos()
        }

        // AddingListeners
        _addlisteners() {
            // Resize listener
            window.addEventListener('resize', this._resizeCallback())
            window.addEventListener('majorlayoutChange', () => this._refresh())

            // On view listener
            this.scrollController = ScrollTrigger.create({
                ...this.scrollOptions,
                onEnter: () => {
                    if (this.initialized) return

                    // Creating carousel on view
                    this._makeCarousel()
                    // Flickity listeners
                    this.carousel?.on('lazyLoad', (e, cell) => this._onLazyload(cell))
                    this.carousel?.on('bgLazyLoad', (e, cell) => this._onLazyload(cell))
                    this.carousel?.on('scroll', (e, progress) => this._onCarouselScroll(e, progress))
                    this.carousel?.on('change', (index) => this._onSlideChange(index))
                    site.eventDispatcher('majorlayoutChange')
                    this.initialized = true

                    // Lazyload native listener, mainly for bg lazyloading
                    this.$slider.addEventListener('lazyloadedImage', (e) => this._refresh())
                }
            })

            

            // Adding to main scroll scene pool
            addScrollController(this.scrollController)


        }
    }

    return {
        FlickityCarousel
    }
})()